import React, { useState, useEffect } from 'react';
import { useAppStore } from '../../store';
import UserManagement from '../admin/UserManagement';
import TokenPoolManagement from '../admin/TokenPoolManagement';

interface ProfileModalProps {
  show: boolean;
  onHide: () => void;
}

const ProfileModal: React.FC<ProfileModalProps> = ({ show, onHide }) => {
    const { user, updateProfile, logout } = useAppStore();
    const [activeTab, setActiveTab] = useState('profile');
    const [formData, setFormData] = useState({
      username: '',
      new_password: '',
      confirm_password: '',
      qianwen_api_key: '',
      github_token: '',
      llm_base_url: '',
      llm_model_name: ''
    });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activeAdminView, setActiveAdminView] = useState('users'); // 'users' | 'tokens' | ''
  const [initialFormSnapshot, setInitialFormSnapshot] = useState<string>('');

    useEffect(() => {
      if (user && show) {
        const nextFormData = {
          username: user.username || '',
          new_password: '',
          confirm_password: '',
          qianwen_api_key: '',
          github_token: '',
          llm_base_url: user.llm_base_url || '',
          llm_model_name: user.llm_model_name || ''
        };
        setFormData(nextFormData);
        setInitialFormSnapshot(JSON.stringify(nextFormData));
        setError('');
        setSuccess('');
        setActiveTab('profile');
        setShowDeleteConfirm(false);
        setActiveAdminView('users');
      }
    }, [user, show]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // 验证密码
      if (formData.new_password && formData.new_password !== formData.confirm_password) {
        setError('两次输入的密码不一致');
        return;
      }

      // 准备更新数据
      const updateData: any = {};

        if (formData.username !== user?.username) {
          updateData.username = formData.username;
        }
  
        if (formData.new_password) {
          updateData.new_password = formData.new_password;
        }
  
        if (formData.qianwen_api_key) {
          updateData.qianwen_api_key = formData.qianwen_api_key;
        }
  
        if (formData.github_token) {
          updateData.github_token = formData.github_token;
        }

        // LLM Base URL & 模型名：仅在有变更时发送
        if (formData.llm_base_url !== (user?.llm_base_url || '')) {
          updateData.llm_base_url = formData.llm_base_url;
        }

        if (formData.llm_model_name !== (user?.llm_model_name || '')) {
          updateData.llm_model_name = formData.llm_model_name;
        }

      if (Object.keys(updateData).length === 0) {
        setError('请修改至少一个字段后再保存');
        return;
      }

      const success = await updateProfile(updateData);

      if (success) {
        setSuccess('更新成功！');
          setFormData(prev => ({
            ...prev,
            new_password: '',
            confirm_password: '',
            qianwen_api_key: '',
            github_token: '',
            llm_base_url: prev.llm_base_url,
            llm_model_name: prev.llm_model_name
          }));
      }
    } catch (error: any) {
      setError(error.message || '更新失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    onHide();
  };

  const handleDeleteAccount = async () => {
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }

    try {
      setLoading(true);
      // TODO: 实现账户删除功能
      alert('账户删除功能开发中...');
    } catch (error: any) {
      setError(error.message || '删除账户失败');
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const hasUnsavedChanges = () => {
    if (!initialFormSnapshot) return false;
    const current = {
      username: formData.username,
      new_password: formData.new_password,
      confirm_password: formData.confirm_password,
      qianwen_api_key: formData.qianwen_api_key,
      github_token: formData.github_token,
      llm_base_url: formData.llm_base_url,
      llm_model_name: formData.llm_model_name
    };
    return JSON.stringify(current) !== initialFormSnapshot;
  };

  const handleCloseWithConfirm = () => {
    if (hasUnsavedChanges()) {
      const confirmClose = window.confirm('当前修改尚未保存，确定要关闭吗？');
      if (!confirmClose) return;
    }
    onHide();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleCloseWithConfirm();
    }
  };

  if (!show) return null;

  return (
    <div className={`profile-overlay ${show ? 'show' : ''}`} onClick={handleOverlayClick}>
      <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
        <div className="profile-header">
          <h3>
            <i className="fas fa-user-cog"></i>
            个人设置
          </h3>
          <button className="profile-close" onClick={handleCloseWithConfirm} title="关闭">
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="profile-content">
          {/* 标签页导航 */}
          <div className="profile-tabs">
            <button
              className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('profile');
                setError('');
                setSuccess('');
              }}
            >
              <i className="fas fa-user"></i>
              基本信息
            </button>
            <button
              className={`tab-btn ${activeTab === 'security' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('security');
                setError('');
                setSuccess('');
              }}
            >
              <i className="fas fa-shield-alt"></i>
              安全设置
            </button>
            <button
              className={`tab-btn ${activeTab === 'api' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('api');
                setError('');
                setSuccess('');
              }}
            >
              <i className="fas fa-key"></i>
              API配置
            </button>
            {user?.is_admin && (
              <button
                className={`tab-btn ${activeTab === 'admin' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('admin');
                  setError('');
                  setSuccess('');
                }}
              >
                <i className="fas fa-crown"></i>
                管理员
              </button>
            )}
          </div>

          {/* 可滚动的内容区域 */}
          <div className="profile-scrollable-content">
            {/* 错误和成功提示 */}
            {error && (
              <div className="alert alert-danger" role="alert">
                <i className="fas fa-exclamation-triangle"></i>
                {error}
              </div>
            )}

            {success && (
              <div className="alert alert-success" role="alert">
                <i className="fas fa-check-circle"></i>
                {success}
              </div>
            )}

            {/* 标签页内容 */}
            <form onSubmit={handleSubmit}>
            {activeTab === 'profile' && (
              <div className="tab-content">
                <h4>基本信息</h4>

                <div className="form-group">
                  <label htmlFor="username">
                    <i className="fas fa-user"></i> 用户名
                  </label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    placeholder="请输入用户名"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>
                    <i className="fas fa-phone"></i> 手机号
                  </label>
                  <input
                    type="text"
                    value={user?.phone || ''}
                    disabled
                    className="form-control-disabled"
                  />
                  <small className="form-text">手机号不可修改</small>
                </div>

                <div className="form-group">
                  <label>
                    <i className="fas fa-calendar"></i> 注册时间
                  </label>
                  <input
                    type="text"
                    value={user?.created_at ? new Date(user.created_at).toLocaleString('zh-CN') : ''}
                    disabled
                    className="form-control-disabled"
                  />
                </div>

                <div className="form-group">
                  <label>
                    <i className="fas fa-clock"></i> 最后登录
                  </label>
                  <input
                    type="text"
                    value={user?.last_login ? new Date(user.last_login).toLocaleString('zh-CN') : '从未登录'}
                    disabled
                    className="form-control-disabled"
                  />
                </div>

                {user?.is_admin && (
                  <div className="admin-badge">
                    <i className="fas fa-crown"></i>
                    管理员账户
                  </div>
                )}
              </div>
            )}

            {activeTab === 'security' && (
              <div className="tab-content">
                <h4>安全设置</h4>

                <div className="form-group">
                  <label htmlFor="new_password">
                    <i className="fas fa-lock"></i> 新密码
                  </label>
                  <input
                    type="password"
                    id="new_password"
                    name="new_password"
                    value={formData.new_password}
                    onChange={handleInputChange}
                    placeholder="请输入新密码（留空则不修改）"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirm_password">
                    <i className="fas fa-lock"></i> 确认新密码
                  </label>
                  <input
                    type="password"
                    id="confirm_password"
                    name="confirm_password"
                    value={formData.confirm_password}
                    onChange={handleInputChange}
                    placeholder="请再次输入新密码"
                  />
                </div>

                <div className="security-actions">
                  <button
                    type="button"
                    className="btn btn-warning"
                    onClick={handleLogout}
                  >
                    <i className="fas fa-sign-out-alt"></i>
                    退出登录
                  </button>

                  <button
                    type="button"
                    className={`btn ${showDeleteConfirm ? 'btn-danger' : 'btn-outline-danger'}`}
                    onClick={handleDeleteAccount}
                    disabled={loading}
                  >
                    <i className="fas fa-trash"></i>
                    {showDeleteConfirm ? '确认删除账户' : '注销账户'}
                  </button>

                  {showDeleteConfirm && (
                    <div className="delete-warning">
                      <i className="fas fa-exclamation-triangle"></i>
                      <strong>警告：</strong>删除账户将永久删除您的所有数据，此操作不可恢复！
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary ms-2"
                        onClick={() => setShowDeleteConfirm(false)}
                      >
                        取消
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'api' && (
              <div className="tab-content">
                <h4>API配置</h4>

                  <div className="form-group">
                    <label htmlFor="qianwen_api_key">
                      <i className="fas fa-robot"></i> LLM模型API Key
                    </label>
                  {user?.qianwen_api_key_masked && (
                    <div className="current-value">
                      <small className="text-muted">当前值: {user.qianwen_api_key_masked}</small>
                    </div>
                  )}
                  <input
                    type="password"
                    id="qianwen_api_key"
                    name="qianwen_api_key"
                    value={formData.qianwen_api_key}
                    onChange={handleInputChange}
                    placeholder={user?.has_qianwen_key ? "输入新值以更新" : "请输入LLM模型API Key"}
                  />
                  <small className="form-text">
                    用于 AI 功能，请配置兼容 OpenAI Chat Completions 协议的服务密钥。
                    推荐使用免费的
                    <a href="https://modelscope.cn/my/myaccesstoken" target="_blank" rel="noopener noreferrer">
                      魔搭 ModelScope
                    </a>
                    推理服务获取 Token。
                  </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="llm_base_url">
                      <i className="fas fa-link"></i> LLM Base URL
                    </label>
                    <input
                      type="text"
                      id="llm_base_url"
                      name="llm_base_url"
                      value={formData.llm_base_url}
                      onChange={handleInputChange}
                      placeholder={user?.is_paid ? "付费用户可留空使用平台默认值" : "例如：https://api-inference.modelscope.cn/v1/ 或自建服务地址"}
                    />
                    <small className="form-text">
                      需兼容 OpenAI Chat Completions 接口的地址，结尾通常为 <code>/v1</code>。
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="llm_model_name">
                      <i className="fas fa-brain"></i> 模型名称
                    </label>
                    <input
                      type="text"
                      id="llm_model_name"
                      name="llm_model_name"
                      value={formData.llm_model_name}
                      onChange={handleInputChange}
                      placeholder={user?.is_paid ? "付费用户可留空使用平台默认模型" : "例如：Qwen/Qwen3-235B-A22B-Instruct-2507 或 gpt-4o 等"}
                    />
                    <small className="form-text">
                      请填写与所选 LLM 服务一致的模型名称。
                    </small>
                  </div>

                <div className="form-group">
                  <label htmlFor="github_token">
                    <i className="fab fa-github"></i> GitHub Token
                  </label>
                  {user?.github_token_masked && (
                    <div className="current-value">
                      <small className="text-muted">当前值: {user.github_token_masked}</small>
                    </div>
                  )}
                  <input
                    type="password"
                    id="github_token"
                    name="github_token"
                    value={formData.github_token}
                    onChange={handleInputChange}
                    placeholder={user?.has_github_token ? "输入新值以更新" : "请输入GitHub Personal Access Token"}
                  />
                  <small className="form-text">
                    用于提高API限制，获取方式：
                    <a href="https://github.com/settings/tokens" target="_blank" rel="noopener noreferrer">
                      GitHub Settings → Developer settings → Personal access tokens
                    </a>
                  </small>
                </div>

                <div className="api-status">
                  <div className="status-item">
                    <i className={`fas fa-circle ${user?.has_qianwen_key ? 'text-success' : 'text-danger'}`}></i>
                    LLM模型API: {user?.qianwen_api_key_masked || '未配置'}
                  </div>
                  <div className="status-item">
                    <i className={`fas fa-circle ${user?.has_github_token ? 'text-success' : 'text-danger'}`}></i>
                    GitHub Token: {user?.github_token_masked || '未配置'}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'admin' && user?.is_admin && (
              <div className="tab-content">
                <h4>管理员功能</h4>
                <div className="admin-functions">
                  <button
                    type="button"
                    className={`btn ${activeAdminView === 'users' ? 'btn-primary' : 'btn-secondary'} admin-function-btn`}
                    onClick={() => setActiveAdminView('users')}
                    disabled={activeAdminView === 'users'}
                  >
                    <i className="fas fa-users"></i>
                    用户管理
                  </button>

                  <button
                    type="button"
                    className={`btn ${activeAdminView === 'tokens' ? 'btn-primary' : 'btn-secondary'} admin-function-btn`}
                    onClick={() => setActiveAdminView(activeAdminView === 'tokens' ? 'users' : 'tokens')}
                  >
                    <i className="fas fa-key"></i>
                    Token池管理
                  </button>

                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => alert('系统统计功能开发中...')}
                  >
                    <i className="fas fa-chart-bar"></i>
                    系统统计
                  </button>

                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => alert('系统设置功能开发中...')}
                  >
                    <i className="fas fa-cog"></i>
                    系统设置
                  </button>
                </div>

                {/* 默认显示用户管理 */}
                {activeAdminView === 'users' && (
                  <UserManagement
                    show={true}
                    onHide={() => setActiveAdminView('users')}
                    inline={true}
                  />
                )}

                {/* Token池管理 */}
                {activeAdminView === 'tokens' && (
                  <div className="admin-inline-content">
                    <TokenPoolManagement />
                  </div>
                )}
              </div>
            )}

            {/* 提交按钮 */}
            {(activeTab === 'profile' || activeTab === 'security' || activeTab === 'api') && (
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  <i className="fas fa-save"></i>
                  {loading ? '保存中...' : '保存更改'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={onHide}>
                  <i className="fas fa-times"></i>
                  取消
                </button>
              </div>
            )}
          </form>
          </div>
        </div>
      </div>


    </div>
  );
};

export default ProfileModal;
